import { Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import { Action } from '../actions';
import { dummyAction, showCommonDataFetchError } from '../actions/common';
import {
    ADD_MOBILE_APP,
    AddMobileApp,
    FETCH_APP_DATA,
    FETCH_APP_INFO,
    FETCH_APP_TRACKERS_TEMPLATES,
    FetchAppData,
    FetchAppInfo,
    fetchAppTrackersTemplates,
    FetchAppTrackersTemplates,
    fillAppData,
    showFetchCreateAppError,
    UPDATE_MOBILE_APP,
    updateAppInfo,
    UpdateAppTrackersTemplates,
    updateAppTrackersTemplates,
    UpdateMobileApp
} from '../actions/mobile-app';
import { ApiMethodExecutingError } from '../service/Errors';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { UrlHelpers } from '../service/UrlHelpers';
import { ValidationResultItem } from '../typings/common';
import {
    AppInfo,
    JavaAppTrackersTemplates,
    MobileSysTypes,
    UrlCheckResponse
} from '../typings/mobile-apps';
import { State } from '../typings/state';

const fetchMobileAppStoreInfoEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.ofType(FETCH_APP_INFO).mergeMap(
        (action: FetchAppInfo): Observable<Action> => {
            return Observable.fromPromise(
                deps.restApiManager.getAppInfo(action.link, action.login)
            )
                .map((appInfo: AppInfo) => {
                    return appInfo.errors && appInfo.errors[0].code
                        ? showFetchCreateAppError(appInfo.errors)
                        : updateAppInfo(appInfo);
                })
                .catch((err: ApiMethodExecutingError) => {
                    return Observable.of(showCommonDataFetchError(err));
                });
        }
    );
};

const fetchAppTrackersTemplatesEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.ofType(FETCH_APP_TRACKERS_TEMPLATES).mergeMap(
        (action: FetchAppTrackersTemplates): Observable<Action> => {
            return Observable.fromPromise(
                deps.restApiManager.getTrackerUrlTemplateList(action.login)
            )
                .map(
                    (
                        trackersTemplates: JavaAppTrackersTemplates
                    ): UpdateAppTrackersTemplates => {
                        return updateAppTrackersTemplates(trackersTemplates);
                    }
                )
                .catch((err: ApiMethodExecutingError) => {
                    return Observable.of(showCommonDataFetchError(err));
                });
        }
    );
};

const createMobileAppEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$
        .ofType(ADD_MOBILE_APP)
        .mergeMap((action: AddMobileApp) => {
            const state = store$.value;
            const tracker = state.mobileAppsCreate.appSettings.tracker;
            const { trackerId, trackingSystem } = tracker;
            let trackingUrlForValidation = tracker.url;

            // Временно, исправить в DIRECT-81888
            if (trackingSystem !== MobileSysTypes.other && !trackerId) {
                trackingUrlForValidation = '';
            }

            return Observable.fromPromise(
                deps.restApiManager.urlCheck(trackingUrlForValidation)
            )
                .mergeMap((value: UrlCheckResponse) => {
                    if (value.isValid) {
                        return Observable.fromPromise(
                            deps.restApiManager.createMobileApp(
                                action.login,
                                state.mobileAppsCreate.appSettings
                            )
                        )
                            .map(({ success, errors }) => {
                                if (success) {
                                    action.extOnSaveCallback
                                        ? action.extOnSaveCallback()
                                        : UrlHelpers.openUrl(
                                              UrlHelpers.getMobileAppsListLink(
                                                  action.login
                                              )
                                          );

                                    return dummyAction;
                                } else {
                                    return showFetchCreateAppError(errors);
                                }
                            })
                            .catch((err: ApiMethodExecutingError) => {
                                return Observable.of(
                                    showCommonDataFetchError(err)
                                );
                            });
                    } else {
                        throw {
                            code: value.errorMessage,
                            path: 'trackers[0].url'
                        };
                    }
                })
                .catch((err: ValidationResultItem) => {
                    return Observable.of(showFetchCreateAppError([err]));
                });
        })
        .catch((err: ValidationResultItem) => {
            return Observable.of(showFetchCreateAppError([err]));
        });
};

const updateMobileAppEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$
        .ofType(UPDATE_MOBILE_APP)
        .mergeMap((action: UpdateMobileApp) => {
            const state = store$.value;
            const tracker = state.mobileAppsCreate.appSettings.tracker;
            const { trackerId, trackingSystem } = tracker;
            let trackingUrlForValidation = tracker.url;

            // Временно, исправить в DIRECT-81888
            if (trackingSystem !== MobileSysTypes.other && !trackerId) {
                trackingUrlForValidation = '';
            }

            return Observable.fromPromise(
                deps.restApiManager.urlCheck(trackingUrlForValidation)
            )
                .mergeMap((value: UrlCheckResponse) => {
                    if (value.isValid) {
                        return Observable.fromPromise(
                            deps.restApiManager.updateMobileApp(
                                action.login,
                                state.mobileAppsCreate.appSettings
                            )
                        )
                            .map(({ success, errors }) => {
                                if (success) {
                                    action.extOnSaveCallback
                                        ? action.extOnSaveCallback()
                                        : UrlHelpers.openUrl(
                                              UrlHelpers.getMobileAppsListLink(
                                                  action.login
                                              )
                                          );

                                    return dummyAction;
                                } else {
                                    return showFetchCreateAppError(errors);
                                }
                            })
                            .catch((err: ApiMethodExecutingError) => {
                                return Observable.of(
                                    showCommonDataFetchError(err)
                                );
                            });
                    } else {
                        throw {
                            code: value.errorMessage,
                            path: 'trackers[0].url'
                        };
                    }
                })
                .catch((err: ValidationResultItem) => {
                    return Observable.of(showFetchCreateAppError([err]));
                });
        })
        .catch((err: ValidationResultItem) => {
            return Observable.of(showFetchCreateAppError([err]));
        });
};

const fetchMobileAppEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.ofType(FETCH_APP_DATA).mergeMap((action: FetchAppData) => {
        return Observable.of(fetchAppTrackersTemplates(action.login)).concat(
            Observable.fromPromise(
                deps.restApiManager.getAppData(action.id, action.login)
            )
                .map(fillAppData)
                .catch((err: ApiMethodExecutingError) => {
                    return Observable.of(showCommonDataFetchError(err));
                })
        );
    });
};

export {
    fetchMobileAppStoreInfoEpic,
    createMobileAppEpic,
    fetchAppTrackersTemplatesEpic,
    fetchMobileAppEpic,
    updateMobileAppEpic
};
